import { ChangeEvent, ReactElement, useCallback, useMemo } from 'react';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { useIntl } from 'react-intl';
import { LabelledEntity } from 'shared/types/commonView';
import { FormControl, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { isOptionEqualToValue } from 'components/common/simpleInputFields/common';
import { createLabelLocalizer } from 'helpers/misc';
import { findById } from 'shared/utils/id/findById';
import { selectControlFormSx } from './styles';
import type { SelectControlProps } from './types';

export function SelectControl({
  filter,
  localSetter,
  localValue,
  disabled,
}: SelectControlProps): ReactElement {
  const intl = useIntl();
  const { labelId, available, labels } = filter;

  const getLocalizedLabel = useMemo(
    () => createLabelLocalizer(intl, labels),
    [intl, labels]
  );

  const localizedOptions = useMemo(
    () =>
      available?.map((item) => ({
        _id: item._id,
        label: getLocalizedLabel(item._id, item.label),
      })) ?? [],
    [available, getLocalizedLabel]
  );

  const selectedValue = useMemo(() => {
    if (localValue) {
      const item = findById(available, localValue, 'SelectControl.selectedValue');
      return {
        _id: localValue,
        label: getLocalizedLabel(localValue, item?.label),
      };
    }

    return localizedOptions[0];
  }, [localValue, getLocalizedLabel, localizedOptions, available]);

  const handleChange = useCallback(
    (_: ChangeEvent<unknown>, value: LabelledEntity | null): void => {
      if (value?._id) {
        localSetter(value._id);
      }
    },
    [localSetter]
  );

  return (
    <FormControl variant="standard" sx={selectControlFormSx}>
      <Autocomplete
        options={localizedOptions}
        getOptionLabel={(option: LabelledEntity): string => option.label}
        isOptionEqualToValue={isOptionEqualToValue}
        disabled={disabled}
        onChange={handleChange}
        id={`select-control-for-${labelId}`}
        value={selectedValue}
        renderInput={(params): ReactElement => (
          <TextField
            variant="outlined"
            {...params}
            label={formatTranslation(intl, unsafeTranslationKey(labelId))}
            margin="normal"
            size="small"
          />
        )}
      />
    </FormControl>
  );
}
